
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CountryItem } from '../types';

export const useCountriesActions = (
  countries: CountryItem[],
  setCountries: React.Dispatch<React.SetStateAction<CountryItem[]>>
) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [editData, setEditData] = useState<CountryItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryItem | null>(null);

  const handleToggleActive = async (id: string) => {
    try {
      // Find the country by id (which is mapped to code)
      const countryToUpdate = countries.find(c => c.id === id);
      if (!countryToUpdate) return;
      
      const newActiveState = !countryToUpdate.active;

      // Update using code as the primary key
      const { error } = await supabase
        .from('countries')
        .update({ active: newActiveState })
        .eq('code', countryToUpdate.code);

      if (error) throw error;

      // Update the local state
      setCountries(prevCountries => 
        prevCountries.map(country => 
          country.id === id ? { ...country, active: newActiveState } : country
        )
      );
      
      toast.success(`${countryToUpdate.name} is now ${newActiveState ? 'active' : 'inactive'}`);
    } catch (error) {
      console.error('Error toggling country status:', error);
      toast.error('Failed to update country status');
    }
  };

  const handleEdit = (country: CountryItem) => {
    setEditing(country.id);
    setEditData({ ...country });
  };

  const handleOpenDialog = (country: CountryItem) => {
    setSelectedCountry({ ...country });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedCountry(null);
    setDialogOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!editData) return;
    
    try {
      // Update using code as the primary key, including region_id
      const { error } = await supabase
        .from('countries')
        .update({
          name: editData.name,
          code: editData.code.toUpperCase(),
          region_id: editData.region_id || null,
        })
        .eq('code', editData.code);

      if (error) throw error;

      // Update the local state
      setCountries(prevCountries => 
        prevCountries.map(country => 
          country.id === editData.id ? editData : country
        )
      );
      
      setEditing(null);
      setEditData(null);
      toast.success('Country updated successfully');
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error('Failed to update country');
    }
  };

  const handleSaveCountryDialog = async (updatedCountry: CountryItem) => {
    try {
      // Update using code as the primary key, including region_id
      const { error } = await supabase
        .from('countries')
        .update({
          name: updatedCountry.name,
          active: updatedCountry.active,
          region_id: updatedCountry.region_id || null,
        })
        .eq('code', updatedCountry.code);

      if (error) throw error;

      // Update the local state
      setCountries(prevCountries => 
        prevCountries.map(country => 
          country.code === updatedCountry.code ? updatedCountry : country
        )
      );
      
      handleCloseDialog();
      toast.success(`${updatedCountry.name} updated successfully`);
      return true;
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error('Failed to update country');
      return false;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const countryToDelete = countries.find(c => c.id === id);
      if (!countryToDelete) return false;

      // Delete using code as the primary key
      const { error } = await supabase
        .from('countries')
        .delete()
        .eq('code', countryToDelete.code);

      if (error) throw error;

      // Update the local state
      setCountries(prevCountries => prevCountries.filter(country => country.id !== id));
      
      toast.success(`${countryToDelete.name} has been removed`);
      return true;
    } catch (error) {
      console.error('Error deleting country:', error);
      toast.error('Failed to delete country');
      return false;
    }
  };

  return {
    editing,
    editData,
    dialogOpen,
    selectedCountry,
    setEditData,
    handleToggleActive,
    handleEdit,
    handleSaveEdit,
    handleDelete,
    handleOpenDialog,
    handleCloseDialog,
    handleSaveCountryDialog
  };
};
